export const REPORT_DOWNLOAD_MODES = Object.freeze({
  STANDARD: 'standard',
  AI: 'ai',
  BOTH: 'both',
});

export const REPORT_DOWNLOAD_STATUSES = Object.freeze({
  READY: 'ready',
  NO_DATA: 'no_data',
});

export const buildReportAccess = ({
  canDownloadStandardReport = false,
  canDownloadAiReport = false,
}) => {
  const canOpenReportDialog = Boolean(
    canDownloadStandardReport || canDownloadAiReport
  );
  const isAiModeLocked = Boolean(canDownloadAiReport && !canDownloadStandardReport);
  const canToggleAiMode = Boolean(canDownloadAiReport && canDownloadStandardReport);
  const canDownloadCombinedReport = Boolean(
    canDownloadStandardReport && canDownloadAiReport
  );

  return {
    canDownloadStandardReport: Boolean(canDownloadStandardReport),
    canDownloadAiReport: Boolean(canDownloadAiReport),
    canOpenReportDialog,
    isAiModeLocked,
    canToggleAiMode,
    canDownloadCombinedReport,
    defaultMode: isAiModeLocked
      ? REPORT_DOWNLOAD_MODES.AI
      : REPORT_DOWNLOAD_MODES.STANDARD,
  };
};

export const resolveReportMode = (access, selectedMode) => {
  if (!access?.canDownloadAiReport) {
    return REPORT_DOWNLOAD_MODES.STANDARD;
  }

  if (access.isAiModeLocked) {
    return REPORT_DOWNLOAD_MODES.AI;
  }

  if (
    selectedMode === REPORT_DOWNLOAD_MODES.BOTH &&
    access?.canDownloadCombinedReport
  ) {
    return REPORT_DOWNLOAD_MODES.BOTH;
  }

  return selectedMode === REPORT_DOWNLOAD_MODES.AI
    ? REPORT_DOWNLOAD_MODES.AI
    : REPORT_DOWNLOAD_MODES.STANDARD;
};

export const modeIncludesAiReport = (mode) =>
  mode === REPORT_DOWNLOAD_MODES.AI || mode === REPORT_DOWNLOAD_MODES.BOTH;

export const modeIncludesStandardReport = (mode) =>
  mode === REPORT_DOWNLOAD_MODES.STANDARD || mode === REPORT_DOWNLOAD_MODES.BOTH;

export const resolveAiReportMode = (access, selectedMode) =>
  modeIncludesAiReport(resolveReportMode(access, selectedMode));

export const getReportModeUi = (mode, labels = {}) => {
  switch (mode) {
    case REPORT_DOWNLOAD_MODES.AI:
      return {
        actionLabel: labels.aiAction || 'Download AI PDF',
        successLabel: labels.aiSuccess || 'AI report downloaded successfully.',
        sendToEmailLabel: labels.aiSendToEmail || 'AI report generation started.',
        nounLabel: labels.aiNoun || 'AI report',
      };
    case REPORT_DOWNLOAD_MODES.BOTH:
      return {
        actionLabel: labels.bothAction || 'Download PDF + CSV',
        successLabel:
          labels.bothSuccess || 'PDF + CSV report package downloaded successfully.',
        sendToEmailLabel:
          labels.bothSendToEmail || 'PDF + CSV report generation started.',
        nounLabel: labels.bothNoun || 'PDF + CSV report package',
      };
    case REPORT_DOWNLOAD_MODES.STANDARD:
    default:
      return {
        actionLabel: labels.standardAction || 'Download',
        successLabel: labels.standardSuccess || 'Report downloaded successfully.',
        sendToEmailLabel: labels.standardSendToEmail || 'Report generation started.',
        nounLabel: labels.standardNoun || 'report',
      };
  }
};

export const downloadFileFromUrl = async (downloadUrl) => {
  const link = document.createElement('a');
  const urlFileName = String(downloadUrl || '').split('/').pop() || 'report';
  link.href = downloadUrl;
  link.setAttribute('download', urlFileName);
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();

  return urlFileName;
};

export const normalizeReportResponseStatus = (status) =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

export const isNoDataReportResponse = (responseData) =>
  normalizeReportResponseStatus(responseData?.status) === REPORT_DOWNLOAD_STATUSES.NO_DATA;
